import { ArrowUpRight, Clock3, Mail, MapPin, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { contactDetails } from "../data/moselcars";
import { TIME_SLOTS, formatAppointment, todayIsoDate } from "./AppointmentPicker";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactSection({ prefillVehicle = "", prefillAppointment = null, onOpenPrivacy }) {
  const [status, setStatus] = useState("idle");
  const [reference, setReference] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [confirmationSent, setConfirmationSent] = useState(true);
  const [vehicleDraft, setVehicleDraft] = useState(prefillVehicle);
  const [messageDraft, setMessageDraft] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("flexibel");

  useEffect(() => {
    setVehicleDraft(prefillVehicle);
  }, [prefillVehicle]);

  useEffect(() => {
    if (!prefillAppointment) return;
    setAppointmentDate(prefillAppointment.date || "");
    setAppointmentTime(prefillAppointment.time || "flexibel");
    if (prefillAppointment.label) setMessageDraft(`Ich interessiere mich für den ${prefillAppointment.vehicle} und möchte ihn gern am ${prefillAppointment.label} besichtigen. Bitte bestätigen Sie mir den Termin.`);
  }, [prefillAppointment]);

  const appointmentLabel = formatAppointment(appointmentDate, appointmentTime);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const email = String(formData.get("email"));
    setStatus("sending");
    setCustomerEmail(email);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: String(formData.get("name")),
          email,
          phone: String(formData.get("phone") || "").trim(),
          vehicle: String(formData.get("vehicle") || ""),
          appointment: appointmentLabel,
          message: messageDraft,
          consent: formData.get("consent") === "on"
        })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || "Die Anfrage konnte nicht gesendet werden.");
      setReference(payload.reference);
      setConfirmationSent(payload.confirmation_sent !== false);
      setStatus("success");
      form.reset();
      setVehicleDraft("");
      setMessageDraft("");
      setAppointmentDate("");
      setAppointmentTime("flexibel");
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <section className="contact-section section" id="kontakt" aria-labelledby="contact-title">
      <div className="page-shell">
        <Reveal className="section-heading contact-heading"><div><span className="section-kicker">04 / DIREKTVERBINDUNG</span><h2 id="contact-title">Ihr nächster<br /><em>Kilometer</em> beginnt hier.</h2></div><p>Ob konkrete Fahrzeugfrage, Besichtigung oder Finanzierung: Erzählen Sie uns, was Sie bewegt. Ihre Anfrage geht direkt an Moselcars.</p></Reveal>
        <div className="contact-grid">
          <Reveal className="contact-form-wrap">
            {status === "success" ? (
              <div className="confirmation-card" data-testid="contact-confirmation-card" role="status">
                <span className="section-kicker">ANFRAGE ERHALTEN</span>
                <h3>Danke. Wir melden uns persönlich.</h3>
                <p>Ihre Anfrage ist bei Moselcars ({contactDetails.email}) eingegangen.{confirmationSent ? <> Eine Bestätigung geht an <strong>{customerEmail}</strong>.</> : <> Eine Bestätigungsmail an <strong>{customerEmail}</strong> konnte nicht zugestellt werden – bitte prüfen Sie die Adresse. Ihre Anfrage ist dennoch angekommen.</>}</p>
                <div className="confirmation-reference"><span>REFERENZ</span><strong data-testid="contact-reference">{reference}</strong></div>
                <p className="confirmation-note">Bitte antworten Sie auf die Bestätigung, falls Sie noch Informationen ergänzen möchten.</p>
                <button className="text-action" type="button" onClick={() => setStatus("idle")} data-testid="contact-new-inquiry-button">Neue Anfrage <ArrowUpRight size={15} /></button>
              </div>
            ) : (
              <>
                <div className="form-topline"><span className="mono-label">ANFRAGE · E-MAIL</span><span>Direkt an {contactDetails.email}</span></div>
                <form className="contact-form" onSubmit={handleSubmit}>
                  <label htmlFor="contact-name">Ihr Name<input id="contact-name" name="name" placeholder="Vor- und Nachname" required data-testid="contact-name-input" /></label>
                  <label htmlFor="contact-email">E-Mail-Adresse<input id="contact-email" type="email" name="email" placeholder="name@beispiel.de" required data-testid="contact-email-input" /></label>
                  <label htmlFor="contact-phone">Telefonnummer (optional)<input id="contact-phone" type="tel" name="phone" inputMode="tel" autoComplete="tel" placeholder="z. B. 0651 1234567" maxLength={40} pattern="[0-9+\(\)\.\/ \-]{5,40}" title="Bitte eine gültige Telefonnummer eingeben" data-testid="contact-phone-input" /></label>
                  <label htmlFor="contact-vehicle">Fahrzeugwunsch<input id="contact-vehicle" name="vehicle" value={vehicleDraft} onChange={(event) => setVehicleDraft(event.target.value)} placeholder="z. B. Audi A3 Sportback" data-testid="contact-vehicle-input" /></label>
                  <div className="contact-appointment" data-testid="contact-appointment-fields">
                    <label htmlFor="contact-appointment-date">Wunschtermin Besichtigung (optional)<input id="contact-appointment-date" type="date" min={todayIsoDate()} value={appointmentDate} onChange={(event) => setAppointmentDate(event.target.value)} data-testid="contact-appointment-date-input" /></label>
                    <label htmlFor="contact-appointment-time">Uhrzeit<select id="contact-appointment-time" value={appointmentTime} onChange={(event) => setAppointmentTime(event.target.value)} disabled={!appointmentDate} data-testid="contact-appointment-time-select"><option value="flexibel">Nach Absprache</option>{TIME_SLOTS.map((slot) => <option key={slot} value={slot} label={`${slot} Uhr`} />)}</select></label>
                  </div>
                  {appointmentLabel && <p className="contact-appointment-preview" data-testid="contact-appointment-preview">Wunschtermin: <strong>{appointmentLabel}</strong> · wird mit der Anfrage übermittelt</p>}
                  <label htmlFor="contact-message">Ihre Nachricht<textarea id="contact-message" name="message" rows="4" value={messageDraft} onChange={(event) => setMessageDraft(event.target.value)} placeholder="Wobei dürfen wir Sie begleiten?" required minLength={10} data-testid="contact-message-input" /></label>
                  <label className="consent-field" htmlFor="contact-consent"><input id="contact-consent" type="checkbox" name="consent" required data-testid="contact-consent-input" /><span>Ich stimme zu, dass Moselcars meine Angaben zur Bearbeitung dieser Anfrage per E-Mail verarbeitet. <a href="/datenschutz" onClick={(event) => { event.preventDefault(); onOpenPrivacy(); }} data-testid="contact-privacy-link">Datenschutz</a></span></label>
                  <button className="button button-primary form-submit" type="submit" disabled={status === "sending"} data-testid="contact-submit-button"><Send size={16} /> {status === "sending" ? "Wird gesendet …" : "Anfrage senden"}</button>
                  {status === "error" && <p className="contact-error" role="alert" data-testid="contact-error-message">Die Anfrage konnte gerade nicht gesendet werden. Bitte versuchen Sie es erneut oder schreiben Sie direkt an {contactDetails.email}.</p>}
                </form>
              </>
            )}
          </Reveal>
          <Reveal className="contact-aside" delay="120ms">
            <div className="map-card">
              <iframe className="google-map-frame" title="Moselcars Standort in Schweich-Issel" src={`https://www.google.com/maps?q=${encodeURIComponent(contactDetails.mapQuery)}&output=embed`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="google-maps-embed" />
              <div className="map-coordinates">{contactDetails.coordinates}</div>
              <div className="map-caption"><span className="mono-label">MOSELCARS · STANDORT</span><strong>Schweich-Issel</strong><span>{contactDetails.address}</span></div>
              <a className="map-link" href={contactDetails.mapsLink} target="_blank" rel="noreferrer" data-testid="google-maps-link">In Google Maps öffnen <MapPin size={15} /></a>
            </div>
            <div className="contact-detail-list">
              <a href={`mailto:${contactDetails.email}`} data-testid="email-contact-link"><Mail size={17} strokeWidth={1.5} /><span><small>E-Mail</small>{contactDetails.email}</span></a>
              <div data-testid="location-contact-info"><MapPin size={17} strokeWidth={1.5} /><span><small>Standort</small>{contactDetails.address}</span></div>
              <div data-testid="inventory-sync-info"><Clock3 size={17} strokeWidth={1.5} /><span><small>Bestand</small>Stündlich aus der Händlerquelle aktualisiert</span></div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
